import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getMapCases } from "@/lib/api";
import { Seo } from "@/components/Seo";

const CATEGORY_COLORS = {
  Theft: "#C8A24A",
  Forgery: "#7FB3D5",
  Looting: "#C0392B",
  Restitution: "#52BE80",
  Censorship: "#A569BD",
  Fraud: "#E67E22",
  Trafficking: "#EC7063",
};
const OTHER_COLOR = "#8A8578";

const colorFor = (cat) => CATEGORY_COLORS[cat] || OTHER_COLOR;

function popupHtml(p) {
  const meta = [p.category, p.status, p.date_start].filter(Boolean).join(" · ");
  const loc = p.location || p.region || "";
  const val = p.estimated_value;
  const showVal = val && !/^(n\/a|none|unknown|—|-)$/i.test(String(val).trim());
  return `
    <div class="coa-popup">
      <div class="coa-popup-cat" style="color:${colorFor(p.category)}">${p.category || "Case"}</div>
      <a class="coa-popup-title" href="/cases/${p.slug}">${p.title}</a>
      <div class="coa-popup-meta">${meta}</div>
      ${loc ? `<div class="coa-popup-loc">${loc}</div>` : ""}
      ${showVal ? `<div class="coa-popup-val">Est. value: ${val}</div>` : ""}
    </div>`;
}

export default function MapView() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [active, setActive] = useState(null); // active category filter

  useEffect(() => {
    getMapCases().then(setPoints).catch(() => setPoints([]));
  }, []);

  // init map once
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, {
      center: [32, 8],
      zoom: 2,
      minZoom: 2,
      maxZoom: 12,
      worldCopyJump: true,
      scrollWheelZoom: true,
      attributionControl: true,
    });
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
      attribution:
        'Tiles &copy; <a href="https://www.esri.com/">Esri</a> — Esri, DeLorme, NAVTEQ',
      maxZoom: 16,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // render markers whenever data or filter change
  useEffect(() => {
    const group = layerRef.current;
    if (!group) return;
    group.clearLayers();
    const shown = active ? points.filter((p) => p.category === active) : points;
    shown.forEach((p) => {
      const marker = L.circleMarker([p.lat, p.lng], {
        radius: 6,
        color: colorFor(p.category),
        weight: 1.5,
        fillColor: colorFor(p.category),
        fillOpacity: 0.55,
      });
      marker.bindPopup(popupHtml(p), { className: "coa-leaflet-popup", maxWidth: 260 });
      marker.on("mouseover", () => marker.setStyle({ fillOpacity: 0.95, radius: 8 }));
      marker.on("mouseout", () => marker.setStyle({ fillOpacity: 0.55, radius: 6 }));
      group.addLayer(marker);
    });
  }, [points, active]);

  const categories = Object.keys(CATEGORY_COLORS);
  const count = active ? points.filter((p) => p.category === active).length : points.length;

  return (
    <div>
      <Seo title="Atlas of Art Crime" description="An interactive world map of every case in the COA archive — thefts, forgeries, looting, and censorship, plotted where they happened." type="website" />

      <div className="mx-auto max-w-[1100px] px-5 lg:px-10 pt-10">
        <div className="eyebrow text-gold">The Archive</div>
        <h1 className="mt-3 font-serif text-4xl leading-[1.02] tracking-tight text-parchment sm:text-5xl lg:text-6xl">
          Atlas of Art Crime
        </h1>
        <p className="mt-4 max-w-2xl font-serif text-xl leading-snug text-faint">
          Every case in the archive, plotted where it happened. Filter by category, then open any point to read the dossier.
        </p>
      </div>

      {/* Legend / filters */}
      <div className="mx-auto max-w-[1100px] px-5 lg:px-10 mt-8">
        <div className="flex flex-wrap items-center gap-2" data-testid="map-filters">
          <button
            data-testid="map-filter-all"
            onClick={() => setActive(null)}
            className={`flex items-center gap-2 border px-3 py-1.5 font-mono text-xs transition-colors ${
              active === null ? "border-gold text-gold" : "border-line text-faint hover:border-gold/50 hover:text-gold"
            }`}
          >
            All Cases
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`map-filter-${cat.toLowerCase()}`}
              onClick={() => setActive(active === cat ? null : cat)}
              className={`flex items-center gap-2 border px-3 py-1.5 font-mono text-xs transition-colors ${
                active === cat ? "border-gold text-parchment" : "border-line text-faint hover:border-gold/50 hover:text-parchment"
              }`}
            >
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat] }} />
              {cat}
            </button>
          ))}
          <span className="ml-auto font-mono text-xs text-faint" data-testid="map-count">
            {count} {count === 1 ? "case" : "cases"} plotted
          </span>
        </div>
      </div>

      {/* Map */}
      <div className="mx-auto max-w-[1100px] px-5 lg:px-10 py-8">
        <div
          ref={mapEl}
          data-testid="crime-map"
          className="w-full border border-line"
          style={{ height: "68vh", minHeight: 460, background: "#0b0b0d" }}
        />
        <p className="mt-3 font-mono text-xs text-faint">
          Marker positions reflect the primary location of each incident; some are approximate.
        </p>
      </div>
    </div>
  );
}
